import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';

import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';

import { translate } from '@jsverse/transloco';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from 'app/providers/semantria-api/semantria-users.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import {
  GroupAddDialogComponent,
  GroupAddDialogOptions,
} from '../group-add-dialog/group-add-dialog.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() user: SemantriaUser;
  @Input() userList: SemantriaUser[];
  @Input() isLoading: boolean;
  @Input() isAdmin: boolean;

  @Output() userChange = new EventEmitter<SemantriaUser>();
  @Output() permissionChanged = new EventEmitter<void>();
  @Output() groupRemoved = new EventEmitter<void>();
  @Output() reloadUser = new EventEmitter<void>();
  @Output() deleteUser = new EventEmitter<SemantriaUser>();

  firstName: UntypedFormControl;
  lastName: UntypedFormControl;
  email: UntypedFormControl;
  alternateEmail: UntypedFormControl;
  login: UntypedFormControl;
  public showPasswordModal = false;

  allGroups: Group[];
  remainingGroups: Group[];
  userGroups: Group[];
  userMemberships: Map<string, Map<string, string[]>>;

  isCurrentUserSelected: boolean;
  deleteUserTooltip;

  constructor(
    private userService: SemantriaUsersService,
    private groupService: GroupService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private systemService: SystemService,
    private authService: AuthService,
    fb: UntypedFormBuilder
  ) {
    this.remainingGroups = [];
    this.userMemberships = new Map<string, Map<string, string[]>>();
    this.firstName = fb.control('');
    this.lastName = fb.control('');
    this.email = fb.control('', Validators.email);
    this.alternateEmail = fb.control('', Validators.email);
    this.login = fb.control('', Validators.required);
  }

  async ngOnInit() {
    await this.refreshGroups();
  }

  async refreshGroups() {
    if (this.isAdmin) {
      this.allGroups = await this.groupService.getGroups();
      this.fillRemainingGroups();
      this.fillUserGroups();
    }
  }

  ngOnChanges(changes) {
    if (changes.user) {
      if (this.allGroups) {
        this.fillRemainingGroups();
        this.fillUserGroups();
      }
      this.firstName.setValue(this.user.first_name);
      this.lastName.setValue(this.user.last_name);
      this.email.setValue(this.user.email);
      this.alternateEmail.setValue(this.user.alternate_email);
      this.login.setValue(this.user.login);

      const toUpdateMemberships = new Map<string, Map<string, string[]>>();
      toUpdateMemberships.set(
        this.user.user_id,
        new Map<string, string[]>(this.user.user_groups.map((g) => [g, []]))
      );

      const memberships = toUpdateMemberships.get(this.user.user_id);
      for (const perm of this.user.permissions) {
        if (perm.group_id) {
          memberships.get(perm.group_id)?.push(perm.permission_type);
        }
      }

      this.userMemberships = new Map<string, Map<string, string[]>>();
      this.userMemberships = toUpdateMemberships;

      this.authService.getSession().then((session) => {
        this.isCurrentUserSelected = session.info2.user_id === this.user.user_id;
        this.deleteUserTooltip = null;
        if (this.isCurrentUserSelected) {
          this.isCurrentUserSelected = true;
          this.deleteUserTooltip = translate('user-details.cannot-delete-self');
        }
      });
    }
  }

  private fillRemainingGroups() {
    this.remainingGroups = this.allGroups.filter(
      (g) => !this.user.user_groups.some((m) => m === g.group_id)
    );
  }

  private fillUserGroups() {
    this.userGroups = this.allGroups.filter((g) =>
      this.user.user_groups.some((m) => m === g.group_id)
    );
  }

  onPermissionChanged() {
    this.permissionChanged.emit();
  }

  onFirstNameUpdate() {
    this.updateProperty('first_name', this.firstName);
  }

  onLastNameUpdate() {
    this.updateProperty('last_name', this.lastName);
  }

  onEmailUpdate() {
    this.updateProperty('email', this.email);
  }

  onAlternateEmailUpdate() {
    this.updateProperty('alternate_email', this.alternateEmail);
  }

  onLoginUpdate() {
    this.updateProperty('login', this.login);
  }

  copyUserId() {
    this.systemService.copyToClipboard(this.user.user_id);
    this.alertService.showToast(translate('user-details.copied-alert'));
  }

  private updateProperty(prop, control) {
    const original = this.user[prop];
    this.user[prop] = control.value;
    this.updateUser(() => (this.user[prop] = original));
  }

  private updateUser(resetFn) {
    this.isLoading = true;
    this.firstName.disable();
    this.lastName.disable();
    this.email.disable();
    this.login.disable();

    this.userService
      .updateUser(this.user)
      .then(() => {
        this.isLoading = false;
        this.firstName.enable();
        this.lastName.enable();
        this.email.enable();
        this.alternateEmail.enable();
        this.login.enable();

        this.userChange.emit(this.user);
      })
      .catch((err) => {
        this.isLoading = false;
        this.firstName.enable();
        this.lastName.enable();
        this.email.enable();
        this.alternateEmail.enable();
        this.login.enable();

        resetFn();

        this.alertService.showErrorToast(translate('user-details.failed-to-update'), err);
      });
  }

  canResetPassword(): boolean {
    return !this.authService.isReadOnly;
  }

  /** onPasswordResetClicked() {
    this.dialogService
      .showWarningPopup({
        title: `Reset Password?`,
        content: `Do you want to reset this user's password? An email will be sent to the user with instructions`,
        confirmTitle: 'Reset',
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.userService
            .resetPassword(this.user.user_id)
            .then((res) => {
              this.alertService.showToast(
                `Password for user ${this.user.email} reset successfully.`
              );
            })
            .catch((err) => {
              this.alertService.showErrorToast('Failed to reset password for user.', err);
            });
        }
      });
  }*/

  onOpenChangePassword() {
    this.showPasswordModal = true;
  }

  onPasswordModalClosed() {
    this.showPasswordModal = false;
  }

  onAddGroup() {
    this.dialogService
      .showDialog<GroupAddDialogComponent, GroupAddDialogOptions>(
        GroupAddDialogComponent,
        {
          user: this.user,
          allGroups: this.remainingGroups,
        },
        {},
        false,
        true
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (!this.userMemberships.has(this.user.user_id)) {
            this.userMemberships.set(this.user.user_id, new Map<string, string[]>());
          }

          result.forEach((group) => {
            this.userMemberships.get(this.user.user_id).set(group.group_id, []);
            this.user.user_groups = this.user.user_groups.concat(group.group_id);
          });

          this.remainingGroups = this.remainingGroups.filter(
            (group) => !result.some((r) => r === group)
          );
          this.fillUserGroups();
          this.reloadUser.emit();
        }
      });
  }

  onGroupRemoved($event) {
    this.userMemberships.get(this.user.user_id).delete($event.group_id);
    this.remainingGroups = [...this.remainingGroups, $event];
    this.user.user_groups = this.user.user_groups.filter((g) => g !== $event.group_id);
    this.groupRemoved.emit();
  }

  onReloadUser() {
    this.reloadUser.emit();
  }

  onDeleteUser() {
    this.deleteUser.emit(this.user);
  }
}
